import { PLACEHOLDER_VALUE } from '@/constants';
import { useDateFormatter } from '@/hooks';
import StudyContactCell from '@/pages/Studies/components/StudyContactCell';
import { Study } from '@/types';
import { Avatar, Grid, GridItem, Tooltip } from '@nautilusbio/nb-design-system';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import React, { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import styles from '../styles.module.css';

interface StudyMetadataTableProps {
  study: Study;
  emptyText?: string;
}

interface MetadataRow {
  label: string;
  date: string;
  byName: string | null | undefined;
  show?: boolean;
}

/**
 * Study metadata header. Renders two adjacent two-row groups, matching the
 * Study Detail mockup:
 *   Modified / Created      Contact / Organization
 * The date group shows: Label | Date | Avatar (tooltip = full name).
 * Uses the user's date format preference from the app store via useDateFormatter.
 */
export function StudyMetadataTable({ study, emptyText = PLACEHOLDER_VALUE }: StudyMetadataTableProps) {
  const { t } = useTranslation(['studies', 'general']);
  const formatDate = useDateFormatter();

  const metadataRows: MetadataRow[] = useMemo(
    () => [
      {
        label: t('general:updated'),
        date: formatDate(study?.updated_at) || emptyText,
        byName: study?.updated_by_full_name,
        show: !!study?.updated_at,
      },
      {
        label: t('general:created'),
        date: formatDate(study?.created_at) || emptyText,
        byName: study?.created_by_full_name,
        show: true,
      },
    ],
    [emptyText, formatDate, study, t],
  );

  // Determine if any visible row has byName data to adjust grid columns
  const visibleRows = metadataRows.filter(row => row.show);
  const hasAnyByName = visibleRows.some(row => row.byName);
  const gridTemplate = hasAnyByName ? 'auto 1fr auto' : 'auto 1fr';

  // Map the study's Contact to the { name, email } shape StudyContactCell expects.
  const contactValue = useMemo(() => {
    if (!study?.contact) return null;
    const name = `${study.contact.first_name ?? ''} ${study.contact.last_name ?? ''}`.trim();
    return { name: name.length > 0 ? name : null, email: study.contact.email };
  }, [study?.contact]);

  return (
    <Stack alignItems="start" gap={10} orientation="horizontal">
      <Grid
        alignItems="center"
        className={styles.metadataGrid}
        gap={2}
        gridTemplateColumns={gridTemplate}
        justifyItems="start"
      >
        {visibleRows.map((row, index) => (
          <Fragment key={`${row.label}-${index}`}>
            <GridItem className={styles.metadataLabel}>
              <Text as="strong" noWrap>
                {row.label}:
              </Text>
            </GridItem>

            <GridItem className={styles.metadataValue}>
              <Text as="span" noWrap>
                {row.date}
              </Text>
            </GridItem>

            {row?.byName ? (
              <GridItem className={styles.metadataAvatar}>
                <Tooltip
                  content={row.byName}
                  label={row.byName}
                  placement="right-start"
                  trigger={
                    <Avatar hasTooltip size="xs3">
                      {row.byName}
                    </Avatar>
                  }
                />
              </GridItem>
            ) : hasAnyByName ? (
              <GridItem className={styles.metadataAvatar} />
            ) : null}
          </Fragment>
        ))}
      </Grid>

      <Grid
        alignItems="center"
        className={styles.metadataGrid}
        gap={2}
        gridTemplateColumns="auto 1fr"
        justifyItems="start"
      >
        <GridItem className={styles.metadataLabel}>
          <Text as="strong" noWrap>
            {t('studies:study.contact')}:
          </Text>
        </GridItem>
        <GridItem className={styles.metadataValue} data-testid="study-detail-contact">
          <StudyContactCell id={study.id} value={contactValue} />
        </GridItem>

        <GridItem className={styles.metadataLabel}>
          <Text as="strong" noWrap>
            {t('studies:study.organization')}:
          </Text>
        </GridItem>
        <GridItem className={styles.metadataValue} data-testid="study-detail-organization">
          <Text as="span" noWrap>
            {study?.org_name || emptyText}
          </Text>
        </GridItem>
      </Grid>
    </Stack>
  );
}
